
import { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

import { LoginScreen } from "@/components/dashboard/LoginScreen";
import { DashboardShell } from "@/components/dashboard/DashboardShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "saniya-prime-dashboard-token";

const Home = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [dashboardData, setDashboardData] = useState(null);
  const [authLoading, setAuthLoading] = useState(Boolean(sessionStorage.getItem(TOKEN_KEY)));
  const [loginLoading, setLoginLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const api = useMemo(() => axios.create({ baseURL: API }), []);

  const fetchDashboardState = useCallback(async (nextToken) => {
    if (!nextToken) {
      setDashboardData(null);
      setAuthLoading(false);
      return;
    }

    setAuthLoading(true);

    try {
      const response = await api.get("/dashboard/state", {
        headers: {
          Authorization: `Bearer ${nextToken}`,
        },
      });

      setDashboardData(response.data);
    } catch (error) {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken("");
      setDashboardData(null);
      toast.error("Session expired. Please unlock the dashboard again.");
    } finally {
      setAuthLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchDashboardState(token);
  }, [fetchDashboardState, token]);

  const handleLogin = async (password) => {
    setLoginLoading(true);

    try {
      const response = await api.post("/dashboard/login", { password });
      sessionStorage.setItem(TOKEN_KEY, response.data.token);
      setToken(response.data.token);
      toast.success("Prime desk unlocked.");
      return { ok: true };
    } catch (error) {
      toast.error("Password rejected. Try again.");
      return { ok: false };
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setDashboardData(null);
    toast.success("Dashboard locked.");
  };

  const handleSaveSettings = async (nextSettings) => {
    if (!token) return;

    setSaveLoading(true);

    try {
      const response = await api.post("/dashboard/settings", nextSettings, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setDashboardData((current) => ({
        ...current,
        settings: response.data.settings,
      }));

      toast.success("Settings sealed to the operator profile.");
    } catch (error) {
      toast.error("Could not save settings.");
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="App bg-[#050505] text-zinc-100">
      <Routes>
        <Route
          path="/"
          element={
            token ? (
              <DashboardShell
                data={dashboardData}
                loading={authLoading}
                onLogout={handleLogout}
                onSaveSettings={handleSaveSettings}
                saveLoading={saveLoading}
              />
            ) : (
              <LoginScreen onLogin={handleLogin} loading={loginLoading} />
            )
          }
        />
        <Route
          path="/*"
          element={
            token ? (
              <DashboardShell
                data={dashboardData}
                loading={authLoading}
                onLogout={handleLogout}
                onSaveSettings={handleSaveSettings}
                saveLoading={saveLoading}
              />
            ) : (
              <LoginScreen onLogin={handleLogin} loading={loginLoading} />
            )
          }
        />
      </Routes>
      <Toaster theme="dark" richColors position="top-right" />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Home />
    </BrowserRouter>
  );
}

export default App;
